import { Button, ConfigProvider, Form, Modal } from 'antd';
import cls from 'classnames';
import { useTranslation } from 'next-i18next';
import { ReactNode, useEffect, useMemo } from 'react';

import CustomRadioGroup from './RadioButtonGroup';
import { fieldMappings } from './metaAreaForm';
import { loadingIcon, retryIcon } from '../../assets';
import { noDataIcon, resetWithoutDataIcon } from '../../assets/MetaArea';
import { useMirrorEnable } from '../../hooks/useMirrorEnable';
import {
  MirrorExperiment,
  setInfoModalVisibility,
  setNpcSettings,
  setUseNpcSettings,
  SuggestNpcSetting,
  updateModifiedFields,
  useAppDispatch,
  useAppSelector,
} from '../../state';
import { track } from '../../track';
import { EModerationDecision } from '../../types';
import { Gender, NpcSettingSuggestScene } from '../../types/meta';
import { isTalkie } from '../../utils';
import { PAGE } from '../EditHeader';
import { ENpcStatusTrackText } from '../Home';
import { getNpcStatus } from '../Home/NpcList';
import { LocalImage } from '../Image';
import { ITextArea } from '../Textarea';
import RichTextArea from './RichTextArea';

interface IInfoModalProps {
  loading?: boolean;
  styles: Record<string, string>;
  isOpen: boolean;
}

const { Item } = Form;

const theme = {
  token: {
    colorBgContainer: '#FFFFFF0A',
    colorBorder: 'transparent',
    colorTextPlaceholder: '#FFFFFF59',
    colorText: '#FFFFFFE5',
    colorTextTertiary: '#FFFFFF59',
    borderRadius: 8,
    lineHeight: 1.5,
    colorSplit: '#F0F0F51A',
    colorBgMask: '#000000B2',
  },
  components: {
    Input: {
      paddingBlock: 12,
      paddingInline: 16,
      inputFontSize: 16,
      activeBorderColor: '#FFFFFF80',
    },
    Form: {
      labelColor: '#FFFFFFE5',
      labelFontSize: 16,
      labelHeight: 22,
      labelRequiredMarkColor: '#FFFFFF59',
      verticalLabelPadding: '0 0 16px',
      itemMarginBottom: 28,
    },
  },
};

const customizeRequiredMark =
  (styles: Record<string, string>) =>
  (label: ReactNode, { required }: { required: boolean }) => (
    <>
      <span>{label}</span>
      {required && <span className={styles.requiredMark}>*</span>}
    </>
  );

export function InfoModal(props: IInfoModalProps) {
  const { t } = useTranslation();

  const { styles, isOpen } = props;
  const [form] = Form.useForm();

  const dispatch = useAppDispatch();
  const {
    npcSettingsLoading: loading,
    npcSettings,
    npcId,
    statusInfo,
    modifiedFields,
  } = useAppSelector((state) => state.edit);
  const status = getNpcStatus(statusInfo);

  const { edit_version, final_decision } = statusInfo;
  const hasEditVersion = edit_version > -1;
  const isVisibleAll = final_decision === EModerationDecision.VisibleAll;
  const Disabled = isVisibleAll && hasEditVersion;

  const isNoEmptyObject = useMemo(() => Object.keys(npcSettings).length > 0, [npcSettings]);

  const genderOptions = [
    { value: Gender.Male, label: t('gender_male') },
    { value: Gender.Female, label: t('gender_female') },
    { value: Gender.NoHuman, label: t('gender_other') },
  ];

  const requiredMark = useMemo(() => customizeRequiredMark(styles), [styles]);

  useEffect(() => {
    form.setFieldsValue(npcSettings);
  }, [form, npcSettings]);

  const handleValuesChange = async (changedValues: any) => {
    dispatch(setNpcSettings(changedValues));

    const updatedModifiedFields: any = { ...modifiedFields };

    Object.keys(changedValues).forEach((key) => {
      const modifiedField = fieldMappings[key];
      if (modifiedField) {
        updatedModifiedFields[modifiedField] = true;
      }
    });

    dispatch(updateModifiedFields(updatedModifiedFields));
  };

  const handleReset = () => {
    if (loading) return;

    // 重试:retry、保存:save、取消：cancel
    track.send('ai_write_popup_click', PAGE, {
      npc_id: npcId,
      ugc_npc_approving_status: ENpcStatusTrackText[status],
      ai_write_click_position: 'name',
      ai_write_popup_click_type: 'retry',
    });

    dispatch(setNpcSettings(null));
    dispatch(
      SuggestNpcSetting({
        params: NpcSettingSuggestScene.NewBasic,
      })
    )
      .unwrap()
      .then((result) => {
        RichTextArea.getRef('edit_info_modal_long_desc')?.reset(result.npc_settings?.long_desc);
      });
    form.resetFields();
  };

  const onCancel = () => {
    // 重试:retry、保存:save、取消：cancel
    track.send('ai_write_popup_click', PAGE, {
      npc_id: npcId,
      ugc_npc_approving_status: ENpcStatusTrackText[status],
      ai_write_click_position: 'name',
      ai_write_popup_click_type: 'cancel',
    });

    dispatch(setNpcSettings(null));
    dispatch(setInfoModalVisibility(false));
    form.resetFields();
  };

  const handleApply = () => {
    // 重试:retry、保存:save、取消：cancel
    track.send('ai_write_popup_click', PAGE, {
      npc_id: npcId,
      ugc_npc_approving_status: ENpcStatusTrackText[status],
      ai_write_click_position: 'name',
      ai_write_popup_click_type: 'save',
    });

    dispatch(setUseNpcSettings(false));
    RichTextArea.getRef('edit_setting_info_long_desc')?.reset(npcSettings.long_desc);
    onCancel();
  };

  const renderLoading = () => (
    <div className={styles.loading}>
      <div className={styles.loadingContent}>
        <LocalImage
          width="40"
          height="40"
          alt="loading icon"
          src={loadingIcon}
          className={styles.loadingIcon}
        />
        <p className={styles.loadingText}>{t('generating')}</p>
      </div>
    </div>
  );

  const renderForm = () => (
    <Form
      name="metaInfo"
      form={form}
      layout="vertical"
      validateTrigger="onBlur"
      requiredMark={requiredMark}
      className={styles.metaInfoForm}
      onValuesChange={handleValuesChange}
    >
      <Item
        name="name"
        label={t('nickname')}
        rules={[{ required: true, message: t('nickname_required') }]}
      >
        <ITextArea
          minRows={1}
          maxRows={1}
          wrapperCls={styles.nameInput}
          placeholder={t('nickname_placeholder')}
          maxLength={isTalkie ? 18 : 9}
          showCount
        />
      </Item>

      <div className={styles.labelContainer}>
        <div className={styles.labelLeft}>
          <span>{t('gender')}</span>
          <span className={styles.requiredMark}>*</span>
        </div>
        <Item
          name="gender"
          trigger="onChange"
          rules={[{ required: true, message: t('gender_required') }]}
          valuePropName="value"
        >
          <CustomRadioGroup disabled={Disabled} options={genderOptions} styles={styles} />
        </Item>
      </div>

      <Item
        label={t('long_desc')}
        name="long_desc"
        rules={[{ required: true, message: t('long_desc_required') }]}
        style={{ marginBottom: 28 }}
      >
        {/* 简介 = 2000 字符（1000 汉字）； */}
        <RichTextArea
          id="edit_info_modal_long_desc"
          initialValue={npcSettings.long_desc}
          nicknameSelector={(state) => state.edit.npcSettings.name}
          minRows={3}
          maxRows={8}
          wrapperCls={styles.ITextArea}
          placeholder={t('long_desc_placeholder')}
          maxLength={isTalkie ? 4000 : 2000}
          showCount
        />
      </Item>

      <Item
        label={t('desc')}
        name="desc"
        rules={[{ required: true, message: t('desc_required') }]}
        style={{ marginBottom: 28 }}
      >
        {/* 简介 = 2000 字符（1000 汉字）； */}
        <ITextArea
          minRows={3}
          maxRows={8}
          wrapperCls={styles.ITextArea}
          placeholder={t('desc_placeholder')}
          maxLength={isTalkie ? 2000 : 1000}
          showCount
        />
      </Item>

      <Item
        name="prologue"
        label={t('prologue')}
        rules={[{ required: true, message: t('prologue_required') }]}
      >
        {/* 500 字符（250 汉字）  */}
        <ITextArea
          minRows={2}
          maxRows={4}
          wrapperCls={styles.ITextArea}
          placeholder={t('prologue_placeholder')}
          maxLength={isTalkie ? 500 : 250}
          showCount
        />
      </Item>
    </Form>
  );

  const renderNoData = () => (
    <div className={styles.empty}>
      <img width="100" height="82" alt="no data" src={noDataIcon.src} />
      <p className={styles.noDataText}>{t('auto_generate_failed')}</p>
      <div className={styles.retry} onClick={handleReset}>
        {t('retry')}
      </div>
    </div>
  );

  if (!isOpen) return null;

  return (
    <ConfigProvider theme={theme}>
      <Modal
        centered
        title={null}
        footer={null}
        open={isOpen}
        width="500px"
        destroyOnClose
        closable={false}
        onCancel={onCancel}
        wrapClassName={styles.infoModal}
      >
        <div className={styles.modalHeader}>
          <h1 className={styles.title}>{t('basic_info')}</h1>
          <section
            className={cls(styles.reset, { [styles.resetUnData]: !isNoEmptyObject || loading })}
            onClick={handleReset}
          >
            <span>{t('retry')}</span>
            <LocalImage
              width="20"
              height="20"
              alt="reset icon"
              src={!isNoEmptyObject || loading ? resetWithoutDataIcon : retryIcon}
            />
          </section>
        </div>

        {loading ? renderLoading() : isNoEmptyObject ? renderForm() : renderNoData()}

        <footer className={styles.footer}>
          <Button className={styles.cancelButton} onClick={onCancel}>
            {t('cancel')}
          </Button>
          <button
            onClick={handleApply}
            disabled={!isNoEmptyObject || loading}
            className={styles.confirmButton}
          >
            {t('apply')}
          </button>
        </footer>
      </Modal>
    </ConfigProvider>
  );
}

export default InfoModal;
